import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import prepareComponent from 'client/shared/decorators/prepare-component'

import urlFor from 'shared/tools/url-helper'
import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'
import { IMPRESSION_FORM_CLEAN } from 'client/bookmate/reducers/impression-form-reducer'

import {
  loadImpression,
  getImpressionById,
} from 'client/bookmate/reducers/impressions-reducer'

import { load as loadComments } from 'client/bookmate/reducers/comments-reducer'

import Meta from 'client/shared/blocks/meta'
import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import MetaTwinBox from 'client/shared/boxes/meta-twin-box'
import MetaDeeplink from 'client/shared/blocks/meta-deeplink'

import { Dispatch, State } from 'shared/types/redux'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { Location } from 'client/shared/types/react-router'
import { ImpressionProps } from 'client/shared/types/impression'
import { CurrentUserState } from 'client/shared/types/current-user'

type Props = {
  dispatch: Dispatch
  app: AppState
  impression: ImpressionProps
  currentUser: CurrentUserState
  location: Location
  children: React.ReactNode
} & withI18nProps

class ImpressionPage extends Component<Props> {
  componentWillUnmount() {
    const { dispatch } = this.props

    dispatch({ type: IMPRESSION_FORM_CLEAN })
  }

  render() {
    if (!this.props.impression) return null

    return (
      <>
        {this.renderMeta()}
        {this.props.children}
      </>
    )
  }

  renderMeta() {
    const {
      i18n,
      app: { host, storedQuery: query },
      currentUser: {
        data: { locale },
      },
      impression,
    } = this.props
    const {
      creator: { name, login },
      uuid: impressionId,
      resource: { language, title, authors, annotation },
      content,
    } = impression
    const authorsStr = authorsAsString(authors)
    const textParams = {
      name,
      title,
      username: login,
      author: authorsStr,
      authors: authorsStr,
    }

    const headTitle = i18n._('impression-form.meta_title', textParams)
    const description =
      i18n._('impression-form.meta_description', textParams) +
      (content ? `: ${content}` : '')
    const ogUrl = `https://${host}${urlFor.impression({
      impressionUuid: impressionId,
      query,
    })}`

    return (
      <>
        <MetaTitle title={headTitle} />
        <MetaDescription description={description} og={annotation} />
        <MetaTwinBox language={language} />
        <Meta content="summary" property="twitter:card" />
        <MetaDeeplink target="impression" uuid={impressionId} />
        <Meta
          content={urlFor.canvasImpression({
            impressionId,
            mode: 'twitter',
            language: locale,
          })}
          property="twitter:image"
        />
        <Meta
          content={urlFor.canvasImpression({
            impressionId,
            mode: 'facebook',
            language: locale,
          })}
          property="og:image"
        />
        <Meta content={ogUrl} property="og:url" />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { impressionId } = params
    const { dispatch } = store
    await Promise.all([
      dispatch(loadImpression(impressionId)),
      dispatch(loadComments('impressions', impressionId)),
    ])
  },
  {
    updateParams: 'impressionId',
  },
)

const connectWrapper = connect(
  (state: State, ownProps: { params: { impressionId: string } }) => {
    const {
      params: { impressionId },
    } = ownProps
    const impression = getImpressionById(state, impressionId)

    return {
      app: state.app,
      currentUser: state.currentUser,
      impression,
    }
  },
)

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withI18n({ update: true }),
)

export default wrappers(ImpressionPage)
